import {setRelayInstructionRules} from '@github-ui/ahp-relay/relay-registry'
import type {CopilotChatReference, CopilotCustomInstructions} from '@github-ui/copilot-chat-types'

import {
  type ContributedInstructionSnapshot,
  deriveInstructionRules,
  normalizeContributedInstructions,
  type RuleFile,
} from './instruction-rules'

export interface InstructionRulesState {
  personalInstructions: string | null
  customInstructions?: CopilotCustomInstructions
  references: CopilotChatReference[]
}

type ResolveReferenceInstructions = (
  references: CopilotChatReference[],
) => CopilotCustomInstructions[] | Promise<CopilotCustomInstructions[]>

type PublishInstructionRules = (rules: RuleFile[]) => void
type PublishContributedInstructions = (instructions: ContributedInstructionSnapshot) => void

const baseInstructionRuleSources = new Map<string, RuleFile[]>()
let baseInstructionRules: RuleFile[] = []
const sessionRepositoryInstructionRules = new Map<string, Map<string, RuleFile>>()
const sessionKeyAliases = new Map<string, string>()
const sessionPublishers = new Set<InstructionRuleSessionPublisher>()
let sessionKeySequence = 0

function resolveSessionKey(key: string): string {
  let resolved = key
  const visited = new Set<string>()
  while (sessionKeyAliases.has(resolved) && !visited.has(resolved)) {
    visited.add(resolved)
    const next = sessionKeyAliases.get(resolved)
    if (!next) break
    resolved = next
  }
  return resolved
}

function discardSessionKey(key: string): string {
  const resolvedKey = resolveSessionKey(key)
  sessionRepositoryInstructionRules.delete(resolvedKey)
  for (const alias of sessionKeyAliases.keys()) {
    if (resolveSessionKey(alias) === resolvedKey) sessionKeyAliases.delete(alias)
  }
  return resolvedKey
}

export function setBaseInstructionRules(rules: RuleFile[], source = 'copilot-chat'): void {
  if (rules.length > 0) {
    baseInstructionRuleSources.set(source, rules)
  } else {
    baseInstructionRuleSources.delete(source)
  }
  const mergedRules = new Map<string, RuleFile>()
  for (const sourceRules of baseInstructionRuleSources.values()) {
    for (const rule of sourceRules) mergedRules.set(rule.name, rule)
  }
  baseInstructionRules = [...mergedRules.values()]
  for (const publisher of sessionPublishers) publisher.publish()
}

export function createInstructionRuleSessionKey(): string {
  sessionKeySequence += 1
  return `quick-chat-${sessionKeySequence}`
}

export function discardInstructionRuleSession(key: string): void {
  discardSessionKey(key)
}

export class InstructionRuleSessionPublisher {
  private selectedRepositoryInstructionRules: RuleFile[] = []
  private activeSessionKey: string | undefined

  publish(): void {
    if (!this.activeSessionKey) return
    const resolvedSessionKey = resolveSessionKey(this.activeSessionKey)
    const mergedRules = new Map(baseInstructionRules.map(rule => [rule.name, rule]))
    for (const rule of sessionRepositoryInstructionRules.get(resolvedSessionKey)?.values() ?? []) {
      mergedRules.set(rule.name, rule)
    }
    for (const rule of this.selectedRepositoryInstructionRules) mergedRules.set(rule.name, rule)
    setRelayInstructionRules(resolvedSessionKey, [...mergedRules.values()])
  }

  setSelectedRepositoryInstructions(customInstructions: CopilotCustomInstructions[]): RuleFile[] {
    this.selectedRepositoryInstructionRules = deriveInstructionRules({
      personalInstructions: null,
      customInstructions,
    })
    this.publish()
    return this.selectedRepositoryInstructionRules.map(rule => ({...rule}))
  }

  activate(key: string, transferFrom?: string): void {
    sessionPublishers.add(this)
    if (transferFrom && transferFrom !== key) {
      const previousKey = resolveSessionKey(transferFrom)
      const nextKey = resolveSessionKey(key)
      const previousRules = sessionRepositoryInstructionRules.get(previousKey)
      if (previousRules) {
        const nextRules = sessionRepositoryInstructionRules.get(nextKey) ?? new Map<string, RuleFile>()
        for (const rule of previousRules.values()) nextRules.set(rule.name, rule)
        sessionRepositoryInstructionRules.set(nextKey, nextRules)
        sessionRepositoryInstructionRules.delete(previousKey)
      }
      sessionKeyAliases.set(transferFrom, nextKey)
      sessionKeyAliases.set(previousKey, nextKey)
    }
    this.activeSessionKey = key
    this.publish()
  }

  deactivate(key: string, discard = false): void {
    const resolvedKey = resolveSessionKey(key)
    const activeResolvedKey = this.activeSessionKey ? resolveSessionKey(this.activeSessionKey) : undefined
    if (discard) discardSessionKey(key)
    if (activeResolvedKey !== resolvedKey) return
    this.selectedRepositoryInstructionRules = []
    if (discard) {
      setRelayInstructionRules(resolvedKey, [])
    } else {
      this.publish()
    }
    this.activeSessionKey = undefined
  }

  getActiveSessionKey(): string | undefined {
    return this.activeSessionKey
  }

  commitRepositoryInstructions(rules: RuleFile[], sessionKey = this.activeSessionKey): void {
    if (!sessionKey || rules.length === 0) return
    const resolvedSessionKey = resolveSessionKey(sessionKey)
    const sessionRules = sessionRepositoryInstructionRules.get(resolvedSessionKey) ?? new Map<string, RuleFile>()
    for (const rule of rules) sessionRules.set(rule.name, {...rule})
    sessionRepositoryInstructionRules.set(resolvedSessionKey, sessionRules)
    if (this.activeSessionKey && resolveSessionKey(this.activeSessionKey) === resolvedSessionKey) this.publish()
  }

  clearSessionRepositoryInstructions(): void {
    if (!this.activeSessionKey || !sessionRepositoryInstructionRules.delete(resolveSessionKey(this.activeSessionKey))) {
      return
    }
    this.publish()
  }

  dispose(): void {
    this.selectedRepositoryInstructionRules = []
    this.activeSessionKey = undefined
    sessionPublishers.delete(this)
  }
}

const defaultSessionPublisher = new InstructionRuleSessionPublisher()

export function setSelectedRepositoryInstructions(customInstructions: CopilotCustomInstructions[]): RuleFile[] {
  return defaultSessionPublisher.setSelectedRepositoryInstructions(customInstructions)
}

export function activateInstructionRuleSession(key: string, transferFrom?: string): void {
  defaultSessionPublisher.activate(key, transferFrom)
}

export function deactivateInstructionRuleSession(key: string, discard = false): void {
  defaultSessionPublisher.deactivate(key, discard)
}

export function getActiveInstructionRuleSessionKey(): string | undefined {
  return defaultSessionPublisher.getActiveSessionKey()
}

export function commitRepositoryInstructions(
  rules: RuleFile[],
  sessionKey = defaultSessionPublisher.getActiveSessionKey(),
): void {
  defaultSessionPublisher.commitRepositoryInstructions(rules, sessionKey)
}

export function clearSessionRepositoryInstructions(): void {
  defaultSessionPublisher.clearSessionRepositoryInstructions()
}

export class InstructionRulesPublisher {
  private generation = 0
  private readonly resolveReferenceInstructions: ResolveReferenceInstructions
  private readonly publishInstructionRules: PublishInstructionRules
  private readonly publishContributedInstructions: PublishContributedInstructions

  constructor(
    resolveReferenceInstructions: ResolveReferenceInstructions,
    publishInstructionRules: PublishInstructionRules = setBaseInstructionRules,
    publishContributedInstructions: PublishContributedInstructions = () => {},
  ) {
    this.resolveReferenceInstructions = resolveReferenceInstructions
    this.publishInstructionRules = publishInstructionRules
    this.publishContributedInstructions = publishContributedInstructions
  }

  async update({personalInstructions, customInstructions, references}: InstructionRulesState): Promise<void> {
    const generation = ++this.generation
    const fallbackInstructions = customInstructions?.type === 'Organization' ? [customInstructions] : []
    if (references.length > 0) {
      const fallbackState = {personalInstructions, customInstructions: fallbackInstructions}
      this.publishInstructionRules(deriveInstructionRules(fallbackState))
      this.publishContributedInstructions(normalizeContributedInstructions(fallbackState))
    }

    let referenceInstructions: CopilotCustomInstructions[]
    try {
      referenceInstructions = await this.resolveReferenceInstructions(references)
    } catch (error) {
      if (generation !== this.generation) return
      // eslint-disable-next-line no-console
      console.error('[copilot-chat] failed to resolve reference custom instructions', error)
      referenceInstructions = []
    }
    if (generation !== this.generation) return

    const assembledInstructions = [...referenceInstructions]
    if (
      customInstructions?.type === 'Organization' &&
      !assembledInstructions.some(instructions => instructions.type === 'Organization')
    ) {
      assembledInstructions.push(customInstructions)
    }

    this.publishInstructionRules(
      deriveInstructionRules({
        personalInstructions,
        customInstructions: assembledInstructions,
      }),
    )
    this.publishContributedInstructions(
      normalizeContributedInstructions({
        personalInstructions,
        customInstructions: assembledInstructions,
      }),
    )
  }

  clear(): void {
    this.generation++
    this.publishInstructionRules([])
    this.publishContributedInstructions([])
  }
}
